import { animationRise } from "asset/framer-motion-util";
import successAnimation from "asset/img/lottie/creation_success.json";
import ButtonBox from "component/ButtonBox";
import CheckBox from "component/CheckBox/CheckBox";
import InputBox from "component/InputBox";
import InputError from "component/InputError";
import InputPassword from "component/InputPassword/InputPassword";
import LinkButton from "component/LinkButton/LinkButton";
import Text from "component/Text";
import JoinTodayViewController from "controller/JoinTodayController";
import { motion } from "framer-motion";
import { RouteProps, withRouter } from "hoc/withRouter";
import { useAppSelector } from "hook/redux";
import React, { useCallback, useEffect, useRef } from "react";
import Lottie from "react-lottie-player";
import { linkPageLogin } from "routes";
import AuthService from "service/Auth.service";
import SimpleReactValidator from "simple-react-validator";
import { ConfigCatFeatureFlag, ViewRenderStatus } from "util/CommonConstant";
import {
  getEnvVariableValue,
  getStandardValidator,
  messageReplace,
  parseStandardAPIErrorMessage,
} from "util/CommonUtil";
import { ENV_VAR } from "util/Constant";
import useIsWhitelabelled from "hook/useIsWhitelabelled";
interface IJoinTodayFormProps extends RouteProps {
  formState?: any;
  formController: JoinTodayViewController;
}

const JoinTodayForm = (props: IJoinTodayFormProps) => {
  const isAuth0Enabled = useAppSelector((state) => state.configCat.enableAuth0);
  const { productName } = useIsWhitelabelled();
  const { formState, formController } = props;

  const isMSAEnabled = useAppSelector((state) => state.configCat[ConfigCatFeatureFlag.ENABLE_MSA]);

  const simpleValidator = useRef(
    new SimpleReactValidator({
      validators: { ...getStandardValidator() },
      ...messageReplace(),
      autoForceUpdate: { forceUpdate: () => formController.forceUpdate(!formState.forceUpdate) },
    })
  );

  useEffect(() => {
    if (!isMSAEnabled) {
      formController.setErrorMessage(""); // Reset error message
    }
  }, [isMSAEnabled]);

  /* 
      -----------------------------------------------------------------------------------------------------------------------
      Validators:
      -----------------------------------------------------------------------------------------------------------------------
    */

  const validate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    simpleValidator?.current?.showMessageFor(e?.target?.name);
  }, []);

  // Function to validate the form
  const validateForm = useCallback(() => {
    let isValid = false; // Initialize isValid flag
    isValid = simpleValidator?.current?.allValid(); // Check if all fields are valid
    if (isValid) {
      // If form is valid
      return true; // Return true
    } else {
      // If form is not valid
      formController.forceUpdate(!formState.forceUpdate); // Force update to show errors
      simpleValidator?.current?.showMessages(); // Show error messages
      formController.forceUpdate(!formState.forceUpdate); // Force update again to ensure errors are displayed
      return false; // Return false
    }
  }, []);

  // Event handlers for form inputs
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      formController.handleInputChange(event);
    },
    [formController]
  );

  const handleCheckBoxChange = useCallback(() => {
    formController.handleAgreementCheck();
  }, [formController]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event?.preventDefault(); // Prevent default form submission behavior

      if (!validateForm()) {
        return Promise.reject(new Error("Form validation failed")); // Reject promise if form validation fails
      }

      if (isMSAEnabled && !formState.data?.isAgreementChecked) {
        formController.setErrorMessage("Master Subscription Agreement not checked"); // Reject promise if agreement is not checked
        return;
      }

      try {
        const joinTodayResponse = await formController.addUser(isAuth0Enabled);
        // Perform login action
        if (joinTodayResponse.response?.error_message === ViewRenderStatus.SUCCESS) {
          console.log("Login success"); // Add any conditions that need to be checked after successful login
        } else {
          formController.setErrorMessage(joinTodayResponse.response?.error_message ?? ""); // Set error message if login response status is not 200
        }
        AuthService.logOut();
      } catch (error: any) {
        const errorMessage =
          parseStandardAPIErrorMessage(error) ?? error?.message ?? "An error occurred"; // Parse and set error message
        formController.setErrorMessage(errorMessage); // Set error message in case of an error
        return Promise.reject(new Error("Error")); // Reject promise with an error message
      }
    },
    [isAuth0Enabled, formController, formState.data?.isAgreementChecked, validateForm]
  ); // Dependencies for the handleSubmit function

  const form = () => {
    return (
      <div className="mb-9 flex flex-col">
        <div className="flex w-full flex-col lg:flex-row lg:gap-6">
          <div className="w-full lg:w-1/2">
            <InputBox
              label="label.text.joinToday.firstName"
              placeholder="Enter First Name"
              name="firstName"
              onChange={handleInputChange}
              value={formState.data?.firstName}
              onBlur={validate}
              validation={simpleValidator?.current?.message(
                "firstName",
                formState.data.firstName,
                "mandatory"
              )}
              autoComplete="new-password"
            />
          </div>

          <div className="w-full lg:w-1/2">
            <InputBox
              label="label.text.joinToday.lastName"
              placeholder="Enter Last Name"
              name="lastName"
              onChange={handleInputChange}
              value={formState.data?.lastName}
              onBlur={validate}
              validation={simpleValidator?.current?.message(
                "lastName",
                formState.data.lastName,
                "mandatory"
              )}
              autoComplete="new-password"
            />
          </div>
        </div>

        <InputBox
          label="label.text.joinToday.email"
          placeholder="Enter email"
          name="email"
          value={formState.data?.email}
          onChange={handleInputChange}
          onBlur={validate}
          validation={simpleValidator?.current?.message(
            "email",
            formState.data.email,
            "mandatory|email|noSpace"
          )}
          disabled={formState.data.email !== ""}
          autoComplete="new-password"
        />

        <InputPassword
          className=""
          label="label.text.joinToday.password"
          placeholder="Enter password"
          name="password"
          onChange={handleInputChange}
          value={formState.data?.password}
          onBlur={validate}
          validation={simpleValidator?.current?.message(
            "password",
            formState.data.password,
            "mandatory"
          )}
          autoComplete="new-password"
        />

        <InputBox
          label="label.text.joinToday.confirmPassword"
          placeholder="Re-enter password"
          name="confirmPassword"
          type="password"
          onChange={handleInputChange}
          value={formState.data?.confirmPassword}
          onBlur={validate}
          validation={simpleValidator?.current?.message(
            "confirmPassword",
            formState.data?.confirmPassword,
            `mandatory|passwordMatch:${formState.data.password}`
          )}
          autoComplete="new-password"
        />
      </div>
    );
  };

  const successContainer = () => {
    return (
      <div className="flex h-full w-full flex-col items-center gap-4">
        <div className="">
          <Lottie
            key={`${formState.email}-success`}
            animationData={successAnimation}
            play
            loop={false}
            style={{ width: 100, height: 100 }}
          />
        </div>

        <Text
          label={"label.text.joinToday.successContainer.title"}
          args={{productName}}
          className="pb-3 text-center text-2xl font-light leading-none dark:text-dark-text-main"
        />

        <Text
          label={"label.text.joinToday.successContainer.description.new"}
          className="pb-3 text-center text-body-copy-2 font-light dark:text-dark-text-main"
        />

        <div className="flex w-full flex-col items-center gap-2">
          <ButtonBox
            label={"component.button.login"}
            onClick={() => props.navigateNoAuth(linkPageLogin)}
          />
        </div>
      </div>
    );
  };

  return (
    <motion.div
      {...animationRise}
      className="w-full rounded-xl bg-light-grey-50 px-10 py-12 shadow-light-black-transparent-25 backdrop-blur-xs dark:bg-dark-login-card dark:shadow-dark-black-transparent-25 sm:w-129 sm:px-16"
    >
      {formState.data?.isUserJoinedSuccessfully ? (
        successContainer()
      ) : (
        <div className="h-full w-full">
          <div className="mb-9 text-center text-heading-2 font-light">
            <Text label="label.text.joinToday.heading" args={{productName}} />
          </div>

          {form()}

          {isMSAEnabled ? (
            <div className="flex w-full items-center justify-center pb-3">
              <CheckBox
                hideErrorRow
                checked={formState.data.isAgreementChecked ?? false}
                onChange={handleCheckBoxChange}
              />

              <div className="flex w-full flex-wrap items-center">
                <Text
                  className="text-body-copy-2 text-light-grey-700 dark:text-dark-text-v2"
                  label={"label.text.joinToday.login.iAgree"}
                />
                &nbsp;
                <LinkButton
                  target="_blank"
                  label={"label.text.joinToday.login.iAgree.label"}
                  path={getEnvVariableValue(ENV_VAR.VITE_MASTER_SUBSCRIPTION_AGREEMENT_LINK)}
                />
              </div>
            </div>
          ) : null}

          <div className="">
            <ButtonBox
              label="label.text.joinToday.button"
              color="gradient-blue"
              onClickWithLoader={handleSubmit}
              // enableMaskWhileLoading
            />
            <InputError
              validation={formState.errorMessage}
              errorClassNames="mt-1 flex justify-center"
            />
          </div>

          <div className="flex w-full items-center justify-center">
            <span className="text-body-copy-2 text-light-grey-900 dark:text-dark-text-v2">
              <Text label={"Already a user?"} />
              &nbsp;
              <LinkButton path={`${linkPageLogin}`} label={"Login"} />
            </span>
          </div>

          {/* <div className="text-body-copy-4 text-dark-text-v2">
        <Text className="" label={"label.text.joinToday.footerText"} />
      </div> */}
        </div>
      )}
    </motion.div>
  );
};

export default withRouter(JoinTodayForm);
